import React, { useEffect, useState } from 'react';
import { ConfirmDialog } from '../../common';
import { EdiService, formatEdiDate } from '../../../services/billing/edi.service';
import type { EdiSettings, SendMode } from '../../../dtos/billing/edi.dto';
import { ErrorNote, errorText, useIsOwner } from './ediUi';

/**
 * SUB-03 send mode. Everyone sees the mode; only the owner changes it, and switching to
 * automatic asks for confirmation (PUT /edi/settings { sendMode: 'auto', confirm: true }).
 */
const SendModeSwitch: React.FC = () => {
  const owner = useIsOwner();
  const [settings, setSettings] = useState<EdiSettings | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    EdiService.getSettings()
      .then((s) => { if (!cancelled) setSettings(s); })
      .catch((err) => { if (!cancelled) setError(errorText(err, 'Failed to load the send mode')); });
    return () => { cancelled = true; };
  }, []);

  const save = async (sendMode: SendMode) => {
    setBusy(true);
    setError(null);
    try {
      setSettings(await EdiService.updateSettings(sendMode === 'auto' ? { sendMode, confirm: true } : { sendMode }));
      setConfirming(false);
    } catch (err) {
      setError(errorText(err, 'Failed to change the send mode'));
    } finally {
      setBusy(false);
    }
  };

  const choose = (mode: SendMode) => {
    if (!settings || mode === settings.sendMode) return;
    if (mode === 'auto') setConfirming(true);
    else void save('review');
  };

  if (!settings) return <ErrorNote message={error} />;

  return (
    <section aria-label="Send mode" className="rounded-lg border border-slate-200 bg-white p-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-medium text-slate-800">Send mode</span>
        {owner ? (
          <div role="radiogroup" aria-label="Send mode" className="flex flex-wrap gap-1">
            {(['review', 'auto'] as const).map((mode) => (
              <button
                key={mode}
                type="button"
                role="radio"
                aria-checked={settings.sendMode === mode}
                disabled={busy}
                onClick={() => choose(mode)}
                className={`rounded-md border px-3 py-1 ${settings.sendMode === mode ? 'border-slate-800 bg-slate-800 text-white' : 'border-slate-300 text-slate-700'}`}
              >
                {mode === 'review' ? 'Review before send' : 'Automatic'}
              </button>
            ))}
          </div>
        ) : (
          <span className="text-slate-700">{settings.sendMode === 'auto' ? 'Automatic' : 'Review before send'}</span>
        )}
        {settings.sendModeSince && <span className="text-xs text-slate-500">since {formatEdiDate(settings.sendModeSince)}</span>}
      </div>
      {!owner && <p className="mt-1 text-xs text-slate-500">Only the agency owner can change the send mode.</p>}
      <div className="mt-2"><ErrorNote message={error} /></div>
      <ConfirmDialog
        open={confirming}
        title="Send claims automatically?"
        message="Ready claims will go to payers each night without a person reviewing the batch. Claims with a high predicted denial risk are still held for review."
        confirmLabel="Switch to automatic"
        busy={busy}
        onConfirm={() => save('auto')}
        onCancel={() => setConfirming(false)}
      />
    </section>
  );
};

export default SendModeSwitch;
